import { ArrowBackIosNewRounded } from '@mui/icons-material';
import { Accordion, AccordionActions, AccordionDetails, AccordionSummary, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { RoutalButton as Button, HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import BackArrowIcon from '../../../../../../../../assets/svg/BackArrowIcon';
import { PlusIcon } from '../../../../../../../../assets/svg/PlusIcon';
import { TrashIcon } from '../../../../../../../../assets/svg/TrashIcon';
import { Modal } from '../../../../../../../../components/Modal';
import { DriversProject } from '../../../../../../../../domain/driversProject/DriversProject';
import { Stop } from '../../../../../../../../domain/stop/Stop';
import { StopTask } from '../../../../../../../../domain/stop/StopTask';
import { RouteStopTaskField } from '../../../../../../../../domain/stop/StopTaskField';
import InputText from '../../../../../../../../react-components/InputText';
import { Text } from '../../../../../../../../react-components/Text';
import { useRetrieveVehicle } from '../../../../../../hooks/useRetrieveVehicle';
import { EditFields } from '../../../../edit-stop/EditFields';
import { TaskFooterButton } from './TaskFooterButton';

const accordionStyles = makeStyles({
  expandIconWrapper: {
    transform: ``,
    transition: `transform 0.2s ease-in-out`,
    '&$expanded': {
      transform: `rotate(-180deg) !important`,
    },
  },
  expanded: {},
  content: {
    margin: `12px 0px !important`,
  },
});

const TaskCreateElement = ({
  stopTask,
  index,
  project,
  updateStopTask,
  deleteTask,
}: {
  stopTask: StopTask;
  index: number;
  project: DriversProject;
  updateStopTask: (stopTask: StopTask) => void;
  deleteTask: () => void;
}) => {
  const [expanded, setExpanded] = useState(false);

  const styleClasses = accordionStyles();
  return (
    <Accordion
      key={index}
      style={{
        width: `100%`,
        display: `flex`,
        flexDirection: `column`,
        justifyContent: `space-between`,
        borderBottom: `1px solid ${RoutalPalette.grey4}`,
        boxShadow: `none`,
        margin: 0,
      }}
      expanded={expanded}
      onChange={(event, expanded) => {
        setExpanded(expanded);
      }}
    >
      <AccordionSummary
        expandIcon={
          <ArrowBackIosNewRounded
            style={{
              fill: RoutalPalette.text,
              opacity: 0.5,
              transform: `rotate(90deg)`,
            }}
          />
        }
        classes={{
          expandIconWrapper: styleClasses.expandIconWrapper,
          expanded: styleClasses.expanded,
          content: styleClasses.content,
        }}
        style={{
          minHeight: `60px`,
          flexDirection: `row`,
          justifyContent: `space-between`,
          borderBottom: `1px solid ${RoutalPalette.grey4}`,
          transition: `height 0.2s ease-in-out`,
        }}
      >
        {stopTask.label ? (
          <Text
            style={{
              fontSize: `16px`,
              color: RoutalPalette.primary80,
              lineHeight: `1.2em`,
              display: expanded ? `block` : `-webkit-box`,
              WebkitLineClamp: 2,
              WebkitBoxOrient: `vertical`,
              overflow: `hidden`,
            }}
          >
            {stopTask.label}
          </Text>
        ) : (
          <Text
            style={{
              fontSize: `16px`,
              color: RoutalPalette.secondary00,
              fontStyle: `italic`,
              width: `100%`,
              lineHeight: `1.2em`,
              display: expanded ? `block` : `-webkit-box`,
              WebkitLineClamp: 2,
              WebkitBoxOrient: `vertical`,
              overflow: `hidden`,
            }}
            intlId="driver.task.label.empty"
          />
        )}
      </AccordionSummary>

      <AccordionDetails>
        <Grid>
          <VSpacer small />

          {/* Task label */}
          <Text style={{ fontSize: `12px` }} variant="label" intlId="driver.stop.report.tasks.label" />
          <VSpacer small />
          <InputText
            fieldValue={stopTask.label}
            onChange={(value) => {
              stopTask.label = value;
              updateStopTask(stopTask);
            }}
            fullWidth
            placeholderIntlId="no_name"
          />

          <VSpacer medium />

          {/* Other task fields */}
          {project.fields.task_fields.map((field) => {
            if (!field.is_custom_field) {
              return (
                <Grid>
                  <Text style={{ fontSize: `12px`, textTransform: `capitalize` }} variant="label">
                    {field.id}
                  </Text>
                  <VSpacer small />
                  {field.id === `barcode` && (
                    <InputText
                      fullWidth
                      fieldValue={(stopTask.fields.find((f) => f.id === field.id)?.value as string) || ``}
                      onChange={(newBarcode) => {
                        const barcodeField = stopTask.fields.find((f) => f.id === field.id);
                        if (!barcodeField) {
                          stopTask.fields.push(
                            RouteStopTaskField.create({
                              id: `barcode`,
                              label: `barcode`,
                              type: `barcode`,
                              value: newBarcode,
                              is_custom_field: false,
                            })
                          );
                          updateStopTask(stopTask);
                          return;
                        }
                        barcodeField.value = newBarcode;
                        updateStopTask(stopTask);
                      }}
                    />
                  )}
                  {field.id === `comments` && (
                    <InputText
                      multiline
                      fullWidth
                      rowsMin={2}
                      fieldValue={(stopTask.fields.find((f) => f.id === field.id)?.value as string) || ``}
                      onChange={(newComment) => {
                        const commentField = stopTask.fields.find((f) => f.id === field.id);
                        if (!commentField) {
                          stopTask.fields.push(
                            RouteStopTaskField.create({
                              id: `comments`,
                              label: `comments`,
                              type: `string`,
                              value: newComment,
                              is_custom_field: false,
                            })
                          );
                          updateStopTask(stopTask);
                          return;
                        }
                        commentField.value = newComment;
                        updateStopTask(stopTask);
                      }}
                    />
                  )}

                  <VSpacer medium />
                </Grid>
              );
            } else {
              let stopTaskField = stopTask.fields.find((f) => {
                return f.id === field.id;
              });
              if (!stopTaskField) {
                stopTaskField = RouteStopTaskField.create({
                  id: field.id,
                  label: field.label,
                  type: field.type,
                  value: field.options && field.options.length > 0 ? [] : ``,
                  is_custom_field: true,
                });

                stopTask.fields.push(stopTaskField);
              }

              return (
                <Grid>
                  <EditFields
                    fieldElement={{
                      ...stopTaskField,
                      options: field.options,
                    }}
                    id={index.toString()}
                    addData={(key: string, newValue: any) => {
                      stopTask.fields.find((f) => {
                        return f.id === field.id;
                      })!.value = newValue;
                      updateStopTask(stopTask);
                    }}
                  />
                </Grid>
              );
            }
          })}
        </Grid>
      </AccordionDetails>

      <AccordionActions
        style={{ display: `flex`, flexDirection: `row`, alignItems: `center`, justifyContent: `flex-end` }}
      >
        <Button variant="text" onClick={deleteTask}>
          <TrashIcon style={{ width: `20px`, height: `20px`, color: `${RoutalPalette.error.medium}` }} />
          <Text
            intlId="driver.task.delete"
            style={{ cursor: `pointer`, fontSize: `12px`, color: `${RoutalPalette.error.medium}` }}
          />
        </Button>
      </AccordionActions>
    </Accordion>
  );
};

interface TaskCreateProps {
  onClose: () => void;
  returnTasks: (tasks: StopTask[]) => void;
  stop: Stop;
  lastId: number;
  footerSize?: number;
}

export const TaskCreate = ({ onClose, returnTasks, stop, lastId, footerSize = 40 }: TaskCreateProps) => {
  const { data: vehicleData } = useRetrieveVehicle();

  const [stopTasks, setStopTasks] = useState<StopTask[]>([]);
  const [nextId, setNextId] = useState<number>(lastId);

  const [showDeleteModal, setShowDeleteModal] = useState<string | undefined>(undefined);
  const [showCloseModal, setShowCloseModal] = useState<boolean>(false);

  useEffect(() => {
    if (stopTasks.length === 0) addNewStopTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!vehicleData) {
    return null;
  }

  const addNewStopTask = () => {
    const newStopTask = StopTask.create({ stop_id: stop.id, status: `pending`, fields: [] }, nextId.toString());
    setNextId(nextId + 1);
    setStopTasks([...stopTasks, newStopTask]);
  };

  const saveStopTasks = () => {
    // Limpiamos los fields que esten vacios
    stopTasks.map((stopTask) => {
      stopTask.fields.map((field) => {
        if (field.value === `` || (Array.isArray(field.value) && field.value.length === 0)) field.value = null;
        return field;
      });

      return stopTask;
    });

    // Creamos un Record con el orden de los
    const taskFields: Record<string, number> = vehicleData.project.fields.task_fields.reduce(
      (prev, curr, index) => ({ ...prev, [curr.id]: index }),
      {}
    );

    stopTasks.map((stopTask) => stopTask.fields.sort((a, b) => taskFields[a.id] - taskFields[b.id]));

    returnTasks(stopTasks);
    onClose();
  };

  return (
    <>
      {/* Modal for leaving if content */}
      <Modal
        open={showCloseModal}
        title={{
          intlId: `drivers.stop.report.modal.title`,
        }}
        subtitle={{
          intlId: `drivers.stop.report.modal.subtitle`,
        }}
        buttons={[
          {
            intlId: `drivers.stop.report.modal.leave`,
            onClick: () => {
              setShowCloseModal(false);
              onClose();
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setShowCloseModal(false);
            },
          },
        ]}
        children={<VSpacer medium />}
      />

      {/* Modal for trying to delete a task */}
      <Modal
        open={!!showDeleteModal}
        title={{
          intlId: `drivers.stop.new_task.modal.title`,
        }}
        subtitle={{
          intlId: `drivers.stop.new_task.modal.subtitle`,
        }}
        buttons={[
          {
            intlId: `drivers.stop.new_task.modal.confirm`,
            onClick: () => {
              setShowDeleteModal(undefined);
              setStopTasks([
                ...stopTasks.filter((st) => {
                  return st.id !== showDeleteModal;
                }),
              ]);
            },
            style: {
              backgroundColor: RoutalPalette.error.medium,
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setShowDeleteModal(undefined);
            },
            variant: `text`,
          },
        ]}
        children={<VSpacer medium />}
      />
      <Grid
        style={{
          backgroundColor: RoutalPalette.white,
          inset: 0,
          position: `absolute`,
          zIndex: `1006`,
        }}
      >
        <Grid style={{ height: `100%`, overflowY: `scroll` }}>
          {/* Header */}
          <Grid
            display={`flex`}
            flexDirection={`row`}
            justifyContent={`flex-start`}
            borderBottom={`1px solid ${RoutalPalette.neutral10}`}
            alignItems={`center`}
            height={`70px`}
            padding={`0px 10px 0px 16px`}
          >
            <Button
              style={{
                backgroundColor: `transparent`,
                minWidth: `fit-content`,
                color: RoutalPalette.grey1,
                display: `flex`,
                alignItems: `center`,
                justifyContent: `center`,
              }}
              onClick={() => {
                if (stopTasks.length > 0) setShowCloseModal(true);
                else onClose();
              }}
            >
              <BackArrowIcon />
            </Button>

            <Text style={{ fontSize: `18px` }} intlId="driver.stop.report.create_task" />
          </Grid>

          {/* Content */}
          <Grid
            container
            direction={`column`}
            wrap={`nowrap`}
            style={{ height: `fit-content`, paddingBottom: `${footerSize}px` }}
          >
            {stopTasks.map((stopTask, index) => (
              <TaskCreateElement
                stopTask={stopTask}
                index={index}
                project={vehicleData.project}
                updateStopTask={(stopTask: StopTask) => {
                  setStopTasks(
                    stopTasks.map((task, i) => {
                      if (i === index) {
                        return stopTask;
                      }
                      return task;
                    })
                  );
                }}
                deleteTask={() => {
                  setShowDeleteModal(stopTask.id);
                }}
              />
            ))}
            <Button
              variant={`flat`}
              style={{
                margin: `20px`,
                display: `flex`,
                flexDirection: `row`,
                alignItems: `center`,
                border: `1px solid ${RoutalPalette.secondary60}`,
              }}
              onClick={() => {
                addNewStopTask();
              }}
            >
              <PlusIcon />
              <HSpacer small />
              <Text style={{ color: RoutalPalette.secondary60 }} intlId="driver.stop.report.add_new_task" />
            </Button>
          </Grid>
        </Grid>

        {/* Footer */}
        <TaskFooterButton
          containerStyle={{ height: `${footerSize}px` }}
          buttonStyle={{ textTransform: `none` }}
          onClick={saveStopTasks}
          buttonIntlId={`driver.create`}
          buttonColor={RoutalPalette.primary40}
        />
      </Grid>
    </>
  );
};
